import React, { useEffect, useMemo } from 'react'
import AvatarItem from './AvatarItem'
import { create } from 'zustand'
import Checkbox from 'components/ui/MyCheckbox'
import Dropdown from 'components/ui/Selector'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

const PID_OPTIONS = [
  { code: 11, name: 'android' },
  { code: 12, name: 'ios' },
  { code: 16, name: 'oc' },
  { code: 17, name: 'gemoji' },
]

export const useCardAtom = create((set) => ({
  info: true,
  badge: true,
  header: true,
  pid: null,
  showReportCount: false,
  toggleInfo: () => set((state) => ({ info: !state.info })),
  toggleBadge: () => set((state) => ({ badge: !state.badge })),
  toggleHeader: () => set((state) => ({ header: !state.header })),
  toggleReportCount: () =>
    set((state) => ({ showReportCount: !state.showReportCount })),
  setShowReportCount: (value) =>
    set(() => ({ showReportCount: Boolean(value) })),
  updatePid: (value) => set((state) => ({ pid: value })),
}))

function DisplayAvatars({
  avatars,
  defaultShowReportCount,
  cardWrapperClassName,
  fillContainer = false,
  onAuthorClick,
  onAidClick,
}) {
  const pid = useCardAtom((state) => state.pid)
  const updatePid = useCardAtom((state) => state.updatePid)
  const setShowReportCount = useCardAtom((state) => state.setShowReportCount)

  useEffect(() => {
    if (defaultShowReportCount !== undefined) {
      setShowReportCount(defaultShowReportCount)
    }
  }, [defaultShowReportCount, setShowReportCount])

  const filterAvatar = useMemo(() => {
    const list = Array.isArray(avatars) ? avatars : []
    if (!pid) return list
    return list.filter((avatar) =>
      avatar?.clientVer?.toString()?.startsWith(pid?.toString()),
    )
  }, [pid, avatars])

  const handleAuthorClick = (authorId) => {
    updatePid(null)
    onAuthorClick?.(authorId)
  }

  const handleAidClick = (avatarId) => {
    updatePid(null)
    onAidClick?.(avatarId)
  }

  const wrapperClassName = cardWrapperClassName
    ? cardWrapperClassName
    : fillContainer
      ? 'min-h-0 flex-1'
      : '!h-[calc(100vh-306px)]'

  return (
    <div
      className={
        fillContainer
          ? 'flex h-full min-h-0 flex-col pt-2'
          : 'flex flex-col pt-2'
      }
    >
      <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="pid-filter" className="text-sm font-medium">
            Pid:
          </label>
          <UpdatePid />
        </div>
        <ToggleHeader />
        <ToggleBadge />
        <ToggleInfo />
        <ToggleReportCount />
      </div>
      <div className={wrapperClassName}>
        <Card className="flex h-full flex-col">
          <CardContent className="min-h-0 flex-1 overflow-hidden pb-0 pl-6 pr-0">
            <div className="h-full overflow-auto">
              <div className="my-6 mr-6 grid grid-cols-9 gap-2 lg:grid-cols-6 xl:grid-cols-9">
                {filterAvatar?.map((avatar, index) => (
                  <AvatarItem
                    avatar={avatar}
                    key={avatar.id}
                    index={index}
                    onAuthorClick={
                      onAuthorClick ? handleAuthorClick : undefined
                    }
                    onAidClick={onAidClick ? handleAidClick : undefined}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

const ToggleHeader = () => {
  const header = useCardAtom((state) => state.header)
  const toggle = useCardAtom((state) => state.toggleHeader)
  return (
    <Checkbox checked={header} onCheckedChange={toggle} label={'show header'} />
  )
}

const ToggleBadge = () => {
  const badge = useCardAtom((state) => state.badge)
  const toggle = useCardAtom((state) => state.toggleBadge)
  return (
    <Checkbox
      checked={badge}
      onCheckedChange={toggle}
      label={'show badge'}
    ></Checkbox>
  )
}

const ToggleInfo = () => {
  const info = useCardAtom((state) => state.info)
  const toggleInfo = useCardAtom((state) => state.toggleInfo)
  return (
    <Checkbox
      checked={info}
      onCheckedChange={toggleInfo}
      label={'show info'}
    ></Checkbox>
  )
}

const ToggleReportCount = () => {
  const show = useCardAtom((state) => state.showReportCount)
  const toggle = useCardAtom((state) => state.toggleReportCount)
  return (
    <Checkbox
      checked={show}
      onCheckedChange={toggle}
      label={'show Number of reports'}
    ></Checkbox>
  )
}

const UpdatePid = () => {
  const pid = useCardAtom((state) => state.pid)
  const updatePid = useCardAtom((state) => state.updatePid)
  return (
    <div className="relative">
      <Dropdown
        datas={PID_OPTIONS}
        showLabel
        showCode
        labelName="Pid"
        value={pid}
        update={updatePid}
        placeholder="All"
      />
      {pid ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Clear pid"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            updatePid(null)
          }}
          className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

export default DisplayAvatars
